import React, { Fragment, useEffect, useState } from "react";

import { components } from "react-select";

import { cx } from "@/utils";
import Button from "@components/Button";
import {
  Dropdown,
  DropdownHeading,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "@components/Dropdown";

import IndeterminateCheckbox from "../IndeterminateCheckbox";
import { UIQuery } from "../types";

import styles from "./FilterControl.module.css";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "@radix-ui/react-popover";

export type IconComponent =
  | React.ComponentType<React.SVGProps<SVGSVGElement>>
  | React.ComponentType<{ className?: string; width?: string | number }>;

export type OpenDropdown = { filterName: string; index: number } | null;

export interface DropdownFilterControlProps {
  value?: string;
  onRemoveControl: () => void;
  singleOption?: boolean;
  onSetValue: (filterName: keyof UIQuery, value: string) => void;
  filterName: keyof UIQuery;
  icon: IconComponent;
  options: Record<string, string>;
  className?: string;
}

export const DropdownFilterControl: React.FC<DropdownFilterControlProps> = ({
  value,
  onRemoveControl,
  singleOption,
  onSetValue,
  filterName,
  icon: Icon,
  options,
  className,
}) => {
  return (
    <Dropdown>
      <DropdownToggle asChild>
        <Button
          // @ts-expect-error expected icon props
          icon={Icon}
          variant="secondary"
          className={cx(styles.dropdown, className)}
        >
          {typeof value === "string" ? options[value] : null}
          <span onClick={onRemoveControl} className={styles.remove}>
            <components.CrossIcon width={14} />
          </span>
        </Button>
      </DropdownToggle>
      {!singleOption && (
        <DropdownMenu>
          {Object.keys(options).map((k) => {
            return (
              <DropdownItem
                key={k}
                onClick={() => {
                  onSetValue(filterName, k);
                }}
              >
                {options[k]}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      )}
    </Dropdown>
  );
};

export interface FilterGroup {
  label?: string;
  keys: string[];
}

export interface HeaderText {
  short: string;
  long: string;
}

export interface FilterControlProps {
  // Identity / structure
  filterName: string;
  index: number;
  header: HeaderText;

  // Data
  values: string[]; // currently selected values
  options: string[]; // allowed option values (value keys)
  labels?: Record<string, string>; // optional mapping value->label
  groups?: FilterGroup[]; // optional grouping

  // Behavior toggles
  required?: boolean;
  canCreateNewOptions?: boolean;

  // Render bits
  icon: IconComponent;
  className?: string;
  testId?: string;
  placeholder?: string;
  distanceComponent?: React.ReactNode;

  // Callbacks (state mgmt is outside; these should return updated values when applicable)
  onRemoveControl: () => void;
  onAddValue: (value: string) => string[]; // returns new values array
  onRemoveValue: (value: string) => string[]; // returns new values array
  onCreateValue: (value: string) => boolean | void; // return false to veto creation

  addError: (filterName: string) => void;
  clearError: (filterName: string) => void;

  // Dropdown coordination across multiple FilterControls
  openDropdown?: OpenDropdown;
  setOpenDropdown: (v: OpenDropdown) => void;
}

export const FilterControl: React.FC<FilterControlProps> = (props) => {
  const [label, setLabel] = useState<string>("");
  const [newValue, setNewValue] = useState<string>("");
  const [isEmptyAndRequired, setIsEmptyAndRequired] = useState<boolean>(false);

  const shouldShowDropdown = (): boolean =>
    props.openDropdown?.filterName === props.filterName &&
    props.openDropdown?.index === props.index;

  const dropdownId = `toggle-${props.filterName}-${props.index}`;

  useEffect(() => {
    const getDefaultLabel = (): string => {
      // Use the labels to generate the label text, this way we have a deterministic order
      const labels = props.values.map((v) => props?.labels?.[v] || v).sort();

      const and = L.MENTIONS_QUERYBUILDER_FILTER_AND_JOINER;
      const more = L.MENTIONS_QUERYBUILDER_FILTER_MORE_SUFFIX;

      if (!labels || labels.length === 0) return props.header.long;
      else if (labels.length <= 2)
        return `${props.header.short} ${labels.join(` ${and} `)}`;
      else {
        const restText = `${and} ${labels.slice(2).length} ${more}`;
        return `${props.header.short} ${labels.slice(0, 2).join(", ")} ${restText}`;
      }
    };

    setLabel(getDefaultLabel());
  }, [props.options, props.header, props.labels, props.values]);

  const handleAddNewOption = (newVal: string | number): void => {
    let didCreateValue = true;

    newVal
      .toString()
      .split(/[,]/)
      .forEach((v) => {
        const result = props.onCreateValue(v);
        if (result === false) {
          didCreateValue = false;
        }
      });

    if (didCreateValue) setNewValue("");
  };

  const onPaste = (evt: React.ClipboardEvent<HTMLInputElement>): void => {
    const clipboard = evt.clipboardData.getData("Text");
    if (!clipboard) return;
    handleAddNewOption(clipboard);
    evt.preventDefault();
  };

  return (
    <Popover open={shouldShowDropdown()}>
      <PopoverTrigger asChild>
        <Button
          id={dropdownId}
          data-testid={props.testId}
          variant="secondary"
          className={cx(styles.dropdownToggle, "pr-0", {
            [styles.error]: isEmptyAndRequired,
          })}
          // @ts-expect-error expected icon props
          icon={props.icon}
          onClick={() => {
            // Handle opening when button is clicked
            if (!shouldShowDropdown()) {
              props.setOpenDropdown({
                filterName: props.filterName,
                index: props.index,
              });
            }
          }}
        >
          {label}

          {props.required ? (
            <span className="p-2 ml-1"></span>
          ) : (
            <span onClick={props.onRemoveControl} className={styles.remove}>
              <components.CrossIcon size={14} />
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContent
          align="start"
          onInteractOutside={(e) => {
            if (
              shouldShowDropdown() &&
              e.type === "dismissableLayer.pointerDownOutside"
            ) {
              props.setOpenDropdown(null);
            }
          }}
          className={cx(
            "bg-white p-3 rounded-lg shadow border border-stone-200",
            {
              [styles.error]: isEmptyAndRequired,
            }
          )}
          data-testid={`${props.testId}-dropdown`}
        >
          <div>
            {props.canCreateNewOptions && (
              <div className="mb-3">
                <input
                  placeholder={
                    props.placeholder ||
                    L.MENTIONS_QUERYBUILDER_ADD_OPTION_PLACEHOLDER
                  }
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onPaste={onPaste}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const target = e.target as HTMLInputElement;
                      handleAddNewOption(target.value);
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  onClick={() => handleAddNewOption(newValue)}
                >
                  {L.MENTIONS_QUERYBUILDER_ADD_OPTION_BUTTON}
                </Button>
              </div>
            )}
            {props.options.length > 1 && (
              <div className="select-all mb-2 border-bottom">
                <IndeterminateCheckbox
                  className="mb-2"
                  data-testid={`check-all-${props.filterName}`}
                  id={`check-all-${props.filterName}-${props.index}`}
                  label={L.MENTIONS_QUERYBUILDER_FILTER_SELECT_ALL}
                  checked={props.values.length === props.options.length}
                  indeterminate={
                    props.values.length > 0 &&
                    props.values.length !== props.options.length
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      props.options.forEach((opt) => {
                        if (!props.values.includes(opt)) props.onAddValue(opt);
                      });
                      if (props.required) {
                        setIsEmptyAndRequired(false);
                        props.clearError(props.filterName);
                      }
                    } else {
                      props.options.forEach((opt) => {
                        if (props.values.includes(opt))
                          props.onRemoveValue(opt);
                      });
                      if (props.required) {
                        setIsEmptyAndRequired(true);
                        props.addError(props.filterName);
                      }
                    }
                  }}
                />
              </div>
            )}

            <div className={styles.options}>
              <div className="mt-0">
                {props.groups
                  ? props.groups.map((group, i) => {
                      // Check so that the actual thing to render is present in the options
                      const renderableItems = group.keys
                        .filter((f) => props.options.includes(f))
                        .sort();
                      // If we've got no items - render no Header either
                      if (!renderableItems.length) return null;

                      return (
                        <Fragment key={i}>
                          {group.label && (
                            <DropdownHeading className="mt-2">
                              {group.label}
                            </DropdownHeading>
                          )}
                          {renderableItems.map((value) => (
                            <div
                              key={`${value}-${props.index}`}
                              className="flex items-center gap-2"
                            >
                              <input
                                id={`check-${value}-${props.index}`}
                                type="checkbox"
                                value={value}
                                onChange={(e) => {
                                  let newValues: string[];
                                  if (e.target.checked) {
                                    newValues = props.onAddValue(value);
                                    if (props.required) {
                                      setIsEmptyAndRequired(false);
                                      props.clearError(props.filterName);
                                    }
                                  } else {
                                    newValues = props.onRemoveValue(value);
                                    if (!newValues.length && props.required) {
                                      setIsEmptyAndRequired(true);
                                      props.addError(props.filterName);
                                    }
                                  }
                                }}
                                checked={props.values.includes(value)}
                              />
                              <label htmlFor={`check-${value}-${props.index}`}>
                                {(props.labels || {})[value] || value}
                              </label>
                            </div>
                          ))}
                        </Fragment>
                      );
                    })
                  : props.options
                      .slice()
                      .sort()
                      .map((value) => (
                        <div
                          key={`${value}-${props.index}`}
                          className="flex items-center gap-2"
                        >
                          <input
                            id={`check-${value}-${props.index}`}
                            type="checkbox"
                            value={value}
                            onChange={(e) => {
                              let newValues: string[];
                              if (e.target.checked) {
                                newValues = props.onAddValue(value);
                                if (props.required) {
                                  setIsEmptyAndRequired(false);
                                  props.clearError(props.filterName);
                                }
                              } else {
                                newValues = props.onRemoveValue(value);
                                if (!newValues.length && props.required) {
                                  setIsEmptyAndRequired(true);
                                  props.addError(props.filterName);
                                }
                              }
                            }}
                            checked={props.values.includes(value)}
                          />
                          <label htmlFor={`check-${value}-${props.index}`}>
                            {(props.labels || {})[value] || value}
                          </label>
                        </div>
                      ))}
              </div>
            </div>
            {props.distanceComponent}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};
